import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import { isWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { isWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { cloneDeep } from "lodash";
import { TWebsiteBuilderDevice } from "pages/secure/facility/websiteBuilder/editor/website/shared/device";
import { isWebsiteBuilderSection } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { isWebsiteBuilderContent } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderContent";

export interface ISetHideableNodeVisibilityActionPayload {
  id: string;
  inverseScrollNodeId: string;
  visible: boolean;
}

export interface ISetHideableNodeVisibilityAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.SET_HIDEABLE_NODE_VISIBILITY;
  payload: ISetHideableNodeVisibilityActionPayload;
}

export function getSetHideableNodeVisibilityInverseAction(action: ISetHideableNodeVisibilityAction): ISetHideableNodeVisibilityAction {
  const {
    id,
    inverseScrollNodeId,
    visible,
  } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.SET_HIDEABLE_NODE_VISIBILITY,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: inverseScrollNodeId,
    payload: {
      id,
      inverseScrollNodeId: action.scrollNodeId,
      visible: !visible,
    },
  };
}

function setHideableNodeVisibilityTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  device: TWebsiteBuilderDevice,
  id: string,
  visible: boolean,
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: IWebsiteBuilderGeneralTraversalNode): IWebsiteBuilderGeneralTraversalNode {
    if (node.id === id) {
      if (isWebsiteBuilderSection(node) && isWebsiteBuilderContent(node)) {
        const updatedNode = cloneDeep(node);
        updatedNode.section.visibility[device] = visible;

        return updatedNode;
      } else if (isWebsiteBuilderContainer(node)) {
        const updatedNode = cloneDeep(node);
        updatedNode.container.visibility[device] = visible;

        return updatedNode;
      } else if (isWebsiteBuilderElement(node)) {
        const updatedNode = cloneDeep(node);
        updatedNode.element.visibility[device] = visible;

        return updatedNode;
      } else {
        return node;
      }
    }
    
    if (!node.children) {
      return node;
    } else {
      return {
        ...node,
        children: node.children.map(traverse),
      };
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}

export function applySetHideableNodeVisibilityChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: ISetHideableNodeVisibilityAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, visible } = action.payload;

  if (id === root.id) {
    return { header, root, footer };
  } else if (id === header.id) {
    return {
      header,
      root: {
        ...root,
        headerVisibility: {
          ...root.headerVisibility,
          [action.device]: visible,
        },
      },
      footer,
    };
  } else if (id === footer.id) {
    return {
      header,
      root: {
        ...root,
        footerVisibility: {
          ...root.footerVisibility,
          [action.device]: visible,
        },
      },
      footer,
    };
  } else {
    return setHideableNodeVisibilityTraversal(header, root, footer, action.device, id, visible);
  }
}
